import { pushGtmEvent } from '@/src/analytics/gtm'
import { selectClevertapProfile } from '@/src/redux/slices/authSlice'
import { useAppSelector } from '@/src/redux/store'
import { getDeviceType } from '@/src/utils/api/getDeviceType'
import { getUserAuthValues } from '@/src/utils/auth'
import { getUserSelectedCurrencyCode } from '@/src/utils/currency/getCurrencyCode'
import { getFormattedDate } from '@/src/utils/date'
import { getPlatformName } from '@/src/utils/deviceType'
import { isUserSignedIn } from '@/src/utils/generalUtils/checkUserLogin'
import { useLocale } from 'next-intl'
import { usePathname } from 'next/navigation'

interface HomepageSearchParams {
    from: string
    to: string
    fromCity?: string
    toCity?: string
    departDate: Date | null
    returnDate: Date | null
    adults: number
    children: number
    infants: number
    flightClass: string
    isRoundTrip: boolean
}

interface MultiCitySegment {
    from: { airportCode: string; city?: string }
    to: { airportCode: string; city?: string }
    date: Date | null
}

const useHomepageGtmEvent = () => {
    const locale = useLocale()
    const pathname = usePathname()
    const isSignedIn = isUserSignedIn()
    const { email } = getUserAuthValues() || {}
    const clevertapProfile = useAppSelector(selectClevertapProfile)
    const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
    const isFlightsPage = pathname?.includes('/flights') || false
    const deviceType = getDeviceType()
    const platform = getPlatformName(deviceType)?.toLowerCase()

    // Attributes common to every homepage-hook event (per the GA Events sheet).
    const commonBase = {
        user_domain: hostname,
        platform,
        user_email: email ? email.toLowerCase() : 'not_informed',
        is_user_logged_in: isSignedIn ? 'yes' : 'no',
        user_language: locale,
        user_currency: getUserSelectedCurrencyCode()?.toLowerCase() || 'not_informed',
        product_category: 'flights',
        user_phone: clevertapProfile?.Phone || 'not_informed'
    }

    // page_view_homepage / page_view_flights_homepage (8 attributes)
    const pushPageViewEvent = () => {
        const eventName = isFlightsPage ? 'page_view_flights_homepage' : 'page_view_homepage'
        pushGtmEvent({ event: eventName, ...commonBase })
    }

    // click_flight_search_button (14 attributes) — airports/dates combined into single fields
    const pushSearchButtonClickEvent = (searchParams: HomepageSearchParams) => {
        const { from, to, fromCity, toCity, departDate, returnDate, adults, children, infants, flightClass, isRoundTrip } =
            searchParams

        const departure = departDate ? getFormattedDate(departDate, 'YYYY/MM/DD') : ''
        const returnFormatted = returnDate ? getFormattedDate(returnDate, 'YYYY/MM/DD') : ''

        pushGtmEvent({
            event: 'click_flight_search_button',
            ...commonBase,
            trip_type: isRoundTrip ? 'round_trip' : 'one_way_trip',
            source_airport: `${fromCity || ''} | ${from.toLowerCase()}`,
            destination_airport: `${toCity || ''} | ${to.toLowerCase()}`,
            journey_dates: returnFormatted ? `${departure} | ${returnFormatted}` : departure,
            travellers_total_count: adults + children + infants,
            flight_class: flightClass.toLowerCase()
        })
    }

    const pushMultiCitySearchButtonClickEvent = (
        segments: MultiCitySegment[],
        adults: number,
        children: number,
        infants: number,
        flightClass: string
    ) => {
        // Multicity: collapse the journey to first-segment origin → last-segment destination.
        const firstSegment = segments[0]
        const lastSegment = segments[segments.length - 1]
        if (!firstSegment?.from.airportCode || !lastSegment?.to.airportCode) {
            return
        }

        const from = firstSegment.from.airportCode
        const to = lastSegment.to.airportCode
        const fromCity = firstSegment.from.city || ''
        const toCity = lastSegment.to.city || ''
        const departure = firstSegment.date ? getFormattedDate(firstSegment.date, 'YYYY/MM/DD') : ''
        const returnFormatted = lastSegment.date ? getFormattedDate(lastSegment.date, 'YYYY/MM/DD') : ''

        pushGtmEvent({
            event: 'click_flight_search_button',
            ...commonBase,
            trip_type: 'multicity_trip',
            source_airport: `${fromCity} | ${from.toLowerCase()}`,
            destination_airport: `${toCity} | ${to.toLowerCase()}`,
            journey_dates: returnFormatted ? `${departure} | ${returnFormatted}` : departure,
            travellers_total_count: adults + children + infants,
            flight_class: flightClass.toLowerCase()
        })
    }

    return {
        pushPageViewEvent,
        pushSearchButtonClickEvent,
        pushMultiCitySearchButtonClickEvent
    }
}

export default useHomepageGtmEvent
export type { HomepageSearchParams }
